import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CircleDot, History } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { API } from '@/utils/api';

const SEASON = { hiver: 'Hiver', ete: 'Été', '4saisons': '4 saisons' };
const ST = { completed: 'Terminée', invoiced: 'Facturée', in_progress: 'En cours', open: 'Ouverte', draft: 'Brouillon' };

export const useClientOverview = (clientId, excludeWo = '') => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!clientId) { setD(null); return; }
    axios.get(`${API}/clients/${clientId}/overview`, { params: { exclude_wo: excludeWo } }).then(r => setD(r.data)).catch(() => setD(null));
  }, [clientId, excludeWo]);
  return d;
};

export const storageLabel = (s) => `${s.qty_total} ${s.tire_type === 'roues_completes' ? 'roues' : 'pneus'} ${SEASON[s.season] || ''} ${s.tire_brand || ''} ${s.tire_dimension || ''}`.replace(/\s+/g, ' ').trim();

const Storage = ({ list }) => (
  <div className="space-y-2" data-testid="client-ctx-storage">{list.map(s => (
    <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
      <CircleDot className="w-5 h-5 text-amber-600 shrink-0" />
      <div className="flex-1 min-w-0"><p className="font-bold">{storageLabel(s)}</p><p className="text-xs text-slate-600">{s.vehicle}{s.notes ? ` · ${s.notes}` : ''}</p></div>
      {s.storage_location && <span className="px-3 py-1 rounded-lg bg-[#0E1116] text-[#FACC15] font-mono font-bold" data-testid={`client-ctx-location-${s.id}`}>{s.storage_location}</span>}
    </div>))}
  </div>
);

const Visits = ({ visits, invoices }) => (
  <div className="space-y-3" data-testid="client-ctx-history">
    {visits.length === 0 && <p className="text-sm text-slate-500">Première visite : aucun passage enregistré.</p>}
    {visits.map(v => (
      <a key={v.id} href={`${window.location.pathname.startsWith('/atelier') ? '/atelier' : '/admin'}/work-orders/${v.id}`} className="block p-2.5 rounded-lg border hover:bg-slate-50" data-testid={`client-ctx-visit-${v.id}`}>
        <div className="flex items-center gap-2 text-sm"><span className="font-mono text-slate-500">{v.date.split('-').reverse().join('.')}</span>
          <span className="font-semibold flex-1 truncate">{v.work || 'Travaux'}</span><span className="text-xs text-slate-500">{ST[v.status] || v.status}</span></div>
        <p className="text-xs text-slate-500">{v.vehicle}{v.mileage ? ` · ${Number(v.mileage).toLocaleString('fr-CH')} km` : ''}{v.invoice_number ? ` · facture ${v.invoice_number}` : ''}</p>
        {v.recommendations && <p className="text-xs text-amber-700 mt-0.5">Conseil : {v.recommendations}</p>}
      </a>))}
    {invoices.length > 0 && <div><p className="text-xs font-bold uppercase text-slate-500 mb-1">Factures</p>
      {invoices.map(i => <p key={i.id} className="text-sm flex gap-2"><span className="font-mono">{i.number}</span><span className="text-slate-500">{i.date.split('-').reverse().join('.')}</span><span className="ml-auto font-semibold">{Number(i.total).toFixed(2)} CHF</span><span className="text-xs text-slate-500 w-16 text-right">{i.status}</span></p>)}</div>}
  </div>
);

export const ClientContextTabs = ({ clientId, excludeWo }) => {
  const d = useClientOverview(clientId, excludeWo);
  const [tab, setTab] = useState(null);
  if (!d) return null;
  const hasStorage = d.storage.length > 0;
  const current = tab || 'history';
  const Btn = ({ k, children, cls }) => (
    <button type="button" onClick={() => setTab(current === k && tab ? null : k)} data-testid={`client-ctx-tab-${k}`}
      className={`px-4 py-2 rounded-t-lg text-sm font-bold flex items-center gap-1.5 border-b-2 transition-colors ${tab === k ? 'border-slate-900 bg-white' : 'border-transparent text-slate-500 hover:text-slate-900'} ${cls || ''}`}>{children}</button>
  );
  return (
    <Card className="p-0 overflow-hidden" data-testid="client-ctx-tabs">
      <div className="flex gap-1 px-2 pt-2 bg-slate-50 border-b">
        <Btn k="history"><History className="w-4 h-4" />Historique ({d.visits.length})</Btn>
        {hasStorage && <Btn k="storage" cls="text-amber-700"><CircleDot className="w-4 h-4" />Gardiennage chez nous ({d.storage.length})</Btn>}
      </div>
      {tab && <div className="p-3">{tab === 'storage' ? <Storage list={d.storage} /> : <Visits visits={d.visits} invoices={d.invoices} />}</div>}
    </Card>
  );
};
